import { useState, useEffect } from 'react'
import { useAuthStore } from '@/stores/auth.store'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Loader2, CreditCard, Calendar, Check, X, Search, AlertTriangle, Monitor, CalendarPlus, Gift } from 'lucide-react'
import { toast } from 'sonner'

interface UserSubscription {
  user_id: number
  email: string
  full_name: string | null
  subscription_status: 'active' | 'free' | 'inactive' | 'expired'
  subscription_start_date: string | null
  subscription_end_date: string | null
  subscription_notes: string | null
  days_remaining: number | null
  has_paid_initial_fee: boolean
  emergency_mode: boolean
  ixen_system_access: boolean
}

interface UpdateSubscriptionData {
  status: string
  extend_days?: number
  notes?: string
}

interface ProviderAccess {
  provider_id: number
  provider_code: string
  provider_name: string
  is_default_included: boolean
  one_time_price: number
  currency: string
  has_access: boolean
  access_type: string | null
  granted_by_admin: string | null
  grant_note: string | null
  granted_at: string | null
}

export const SubscriptionManagement = () => {
  const token = useAuthStore(state => state.token)
  const [users, setUsers] = useState<UserSubscription[]>([])
  const [filteredUsers, setFilteredUsers] = useState<UserSubscription[]>([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [selectedUser, setSelectedUser] = useState<UserSubscription | null>(null)
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [updating, setUpdating] = useState(false)
  const [updateData, setUpdateData] = useState<UpdateSubscriptionData>({
    status: 'active',
    extend_days: 30,
    notes: ''
  })
  const [providerAccess, setProviderAccess] = useState<ProviderAccess[]>([])
  const [loadingProviders, setLoadingProviders] = useState(false)
  const [togglingProvider, setTogglingProvider] = useState<string | null>(null)

  // ── Stato selezione bulk ──────────────────────────────────────────────────
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set())
  const [bulkDays, setBulkDays] = useState<string>('30')
  const [bulkExtending, setBulkExtending] = useState(false)
  const [bulkTrialing, setBulkTrialing] = useState(false)

  const fetchUsers = async () => {
    try {
      const response = await fetch('/api/v1/admin/subscriptions', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })
      if (!response.ok) throw new Error('Failed to fetch users')
      const data = await response.json()
      const usersList = data.users || []
      setUsers(usersList)
      setFilteredUsers(usersList)
    } catch (err) {
      toast.error('Errore nel caricamento utenti')
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { fetchUsers() }, [])

  useEffect(() => {
    let filtered = users
    if (searchQuery) {
      const query = searchQuery.toLowerCase()
      filtered = filtered.filter(user =>
        user.email.toLowerCase().includes(query) ||
        (user.full_name && user.full_name.toLowerCase().includes(query))
      )
    }
    if (statusFilter !== 'all') {
      filtered = filtered.filter(user => user.subscription_status === statusFilter)
    }
    setFilteredUsers(filtered)
    // Rimuovi dalla selezione gli utenti non più visibili
    setSelectedIds(prev => {
      const visibleIds = new Set(filtered.map(u => u.user_id))
      const next = new Set<number>()
      prev.forEach(id => { if (visibleIds.has(id)) next.add(id) })
      return next
    })
  }, [searchQuery, statusFilter, users])

  // ── Helpers selezione ─────────────────────────────────────────────────────
  const allFilteredSelected = filteredUsers.length > 0 && filteredUsers.every(u => selectedIds.has(u.user_id))
  const someFilteredSelected = filteredUsers.some(u => selectedIds.has(u.user_id))

  const toggleSelectAll = () => {
    if (allFilteredSelected) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(filteredUsers.map(u => u.user_id)))
    }
  }

  const toggleSelectUser = (userId: number) => {
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (next.has(userId)) next.delete(userId)
      else next.add(userId)
      return next
    })
  }

  // ── Proroga bulk ──────────────────────────────────────────────────────────
  const handleBulkExtend = async (userIds: number[]) => {
    const days = parseInt(bulkDays)
    if (isNaN(days) || days <= 0) {
      toast.error('Inserisci un numero di giorni valido')
      return
    }
    setBulkExtending(true)
    try {
      const response = await fetch('/api/v1/admin/subscriptions/bulk-extend', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ user_ids: userIds, days })
      })
      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.detail || 'Errore durante la proroga')
      }
      const result = await response.json()
      toast.success(result.message)
      if (result.skipped > 0) {
        toast.warning(`${result.skipped} abbonamenti ignorati (inattivi senza data)`)
      }
      setSelectedIds(new Set())
      fetchUsers()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Errore durante la proroga')
    } finally {
      setBulkExtending(false)
    }
  }

  // ── Helpers UI ────────────────────────────────────────────────────────────
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':   return <Badge className="bg-green-500 hover:bg-green-600">Attivo</Badge>
      case 'free':     return <Badge className="bg-purple-500 hover:bg-purple-600">Prova gratuita</Badge>
      case 'expired':  return <Badge className="bg-yellow-500 hover:bg-yellow-600">Scaduto</Badge>
      case 'inactive': return <Badge className="bg-gray-500 hover:bg-gray-600">Inattivo</Badge>
      default:         return <Badge>{status}</Badge>
    }
  }

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-'
    return new Date(dateString).toLocaleDateString('it-IT', {
      day: '2-digit', month: '2-digit', year: 'numeric'
    })
  }

  const getDaysRemaining = (endDate: string | null) => {
    if (!endDate) return null
    const diffTime = new Date(endDate).getTime() - new Date().getTime()
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24))
  }

  // ── Prova gratuita bulk (7 giorni) ─────────────────────────────────────────
  const handleBulkTrial = async (userIds: number[]) => {
    if (userIds.length === 0) return
    if (!window.confirm(`Attivare una prova gratuita di 7 giorni per ${userIds.length} utente/i selezionato/i?`)) {
      return
    }
    setBulkTrialing(true)
    try {
      const response = await fetch('/api/v1/admin/subscriptions/bulk-trial', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_ids: userIds, days: 7 })
      })
      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.detail || 'Errore durante l\'attivazione della prova')
      }
      const result = await response.json()
      toast.success(result.message || 'Prove gratuite attivate')
      setSelectedIds(new Set())
      fetchUsers()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Errore durante l\'attivazione della prova')
    } finally {
      setBulkTrialing(false)
    }
  }

  const handleEditUser = (user: UserSubscription) => {
    setSelectedUser(user)
    setUpdateData({ status: user.subscription_status, extend_days: 30, notes: user.subscription_notes || '' })
    setProviderAccess([])
    fetchProviderAccess(user.user_id)
    setIsDialogOpen(true)
  }

  const handleUpdateSubscription = async () => {
    if (!selectedUser) return
    setUpdating(true)
    try {
      const response = await fetch(`/api/v1/admin/subscriptions/${selectedUser.user_id}`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(updateData)
      })
      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.detail || 'Failed to update subscription')
      }
      toast.success(`Abbonamento aggiornato per ${selectedUser.email}`)
      setIsDialogOpen(false)
      fetchUsers()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Errore aggiornamento')
    } finally {
      setUpdating(false)
    }
  }

  const fetchProviderAccess = async (userId: number) => {
    setLoadingProviders(true)
    try {
      const response = await fetch(`/api/v1/admin/subscriptions/${userId}/provider-access`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      })
      if (!response.ok) throw new Error('Failed to fetch provider access')
      const data = await response.json()
      setProviderAccess(data)
    } catch (err) {
      toast.error('Errore nel caricamento accesso provider')
    } finally {
      setLoadingProviders(false)
    }
  }

  const handleToggleProviderAccess = async (providerCode: string, currentAccess: boolean) => {
    if (!selectedUser) return
    setTogglingProvider(providerCode)
    try {
      const response = await fetch(`/api/v1/admin/subscriptions/${selectedUser.user_id}/provider-access`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          provider_code: providerCode,
          grant: !currentAccess,
          note: !currentAccess ? 'Attivato manualmente da admin' : 'Revocato da admin'
        })
      })
      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.detail || 'Failed to update provider access')
      }
      const result = await response.json()
      toast.success(result.message)
      fetchProviderAccess(selectedUser.user_id)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Errore aggiornamento provider')
    } finally {
      setTogglingProvider(null)
    }
  }

  const handleToggleEmergency = async (userId: number) => {
    try {
      const response = await fetch(`/api/v1/admin/subscriptions/${userId}/emergency-mode`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      })
      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.detail || 'Failed to toggle emergency mode')
      }
      const result = await response.json()
      toast.success(result.message)
      fetchUsers()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Errore toggle emergenza')
    }
  }

  const handleToggleIxenSystemAccess = async (userId: number) => {
    try {
      const response = await fetch(`/api/v1/admin/subscriptions/${userId}/ixen-system-access`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      })
      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.detail || 'Failed to toggle iXen System access')
      }
      const result = await response.json()
      toast.success(result.message)
      fetchUsers()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Errore toggle iXen System')
    }
  }

  const stats = {
    total:    users?.length || 0,
    active:   users?.filter(u => u.subscription_status === 'active').length || 0,
    free:     users?.filter(u => u.subscription_status === 'free').length || 0,
    expired:  users?.filter(u => u.subscription_status === 'expired').length || 0,
    inactive: users?.filter(u => u.subscription_status === 'inactive').length || 0,
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* KPI */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Totale Utenti</p>
                <p className="text-2xl font-bold">{stats.total}</p>
              </div>
              <CreditCard className="w-8 h-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Abbonamenti Attivi</p>
                <p className="text-2xl font-bold text-green-500">{stats.active}</p>
              </div>
              <Check className="w-8 h-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Prova gratuita</p>
                <p className="text-2xl font-bold text-purple-500">{stats.free}</p>
              </div>
              <Gift className="w-8 h-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Scaduti</p>
                <p className="text-2xl font-bold text-yellow-500">{stats.expired}</p>
              </div>
              <Calendar className="w-8 h-8 text-yellow-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Inattivi</p>
                <p className="text-2xl font-bold text-gray-500">{stats.inactive}</p>
              </div>
              <X className="w-8 h-8 text-gray-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="w-5 h-5" />
            Gestione Abbonamenti
          </CardTitle>
          <CardDescription>
            Gestisci gli abbonamenti mensili degli utenti. Solo gli utenti con abbonamento attivo possono utilizzare le funzionalità di trading.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">

          {/* Filtri */}
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Cerca per email o nome..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Filtra per stato" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti</SelectItem>
                <SelectItem value="active">Attivi</SelectItem>
                <SelectItem value="free">Prova gratuita</SelectItem>
                <SelectItem value="expired">Scaduti</SelectItem>
                <SelectItem value="inactive">Inattivi</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Pannello Proroga Massiva */}
          <div className="rounded-lg border border-blue-500/30 bg-blue-500/5 p-4 space-y-3">
            <div className="flex items-center gap-2">
              <CalendarPlus className="w-4 h-4 text-blue-400" />
              <span className="font-medium text-sm">Proroga Massiva</span>
              {selectedIds.size > 0 && (
                <Badge className="bg-blue-600 hover:bg-blue-700 text-xs">
                  {selectedIds.size} selezionati
                </Badge>
              )}
            </div>

            <div className="flex flex-col sm:flex-row gap-3 items-end">
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Giorni da aggiungere</Label>
                <Input
                  type="number"
                  min={1}
                  max={3650}
                  value={bulkDays}
                  onChange={(e) => setBulkDays(e.target.value)}
                  className="w-32"
                  placeholder="es. 30"
                />
              </div>

              <div className="flex gap-2 flex-wrap">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={selectedIds.size === 0 || bulkExtending}
                  onClick={() => handleBulkExtend(Array.from(selectedIds))}
                >
                  {bulkExtending ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-1" />
                  ) : (
                    <CalendarPlus className="w-4 h-4 mr-1" />
                  )}
                  Applica a selezionati
                  {selectedIds.size > 0 && ` (${selectedIds.size})`}
                </Button>

                <Button
                  variant="outline"
                  size="sm"
                  disabled={filteredUsers.length === 0 || bulkExtending}
                  onClick={() => handleBulkExtend(filteredUsers.map(u => u.user_id))}
                >
                  {bulkExtending ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-1" />
                  ) : (
                    <CalendarPlus className="w-4 h-4 mr-1" />
                  )}
                  Applica a tutti i filtrati
                  {filteredUsers.length > 0 && ` (${filteredUsers.length})`}
                </Button>

                <Button
                  size="sm"
                  className="bg-purple-600 hover:bg-purple-700 text-white"
                  disabled={selectedIds.size === 0 || bulkTrialing}
                  onClick={() => handleBulkTrial(Array.from(selectedIds))}
                  title="Attiva una prova gratuita di 7 giorni (stato 'free' + accesso iXen System) per gli utenti selezionati"
                >
                  {bulkTrialing ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-1" />
                  ) : (
                    <Gift className="w-4 h-4 mr-1" />
                  )}
                  Prova gratuita 7 giorni
                  {selectedIds.size > 0 && ` (${selectedIds.size})`}
                </Button>

                {selectedIds.size > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setSelectedIds(new Set())}
                  >
                    Deseleziona tutto
                  </Button>
                )}
              </div>
            </div>

            <p className="text-xs text-muted-foreground">
              Attivi: la scadenza viene spostata in avanti di N giorni. Scaduti: vengono riattivati con scadenza da oggi + N giorni.
            </p>
          </div>

          {/* Tabella */}
          <div className="border rounded-lg overflow-hidden">
            <table className="w-full">
              <thead className="bg-muted/50">
                <tr>
                  <th className="px-4 py-3 text-left w-10">
                    <Checkbox
                      checked={allFilteredSelected}
                      onCheckedChange={toggleSelectAll}
                      aria-label="Seleziona tutti"
                      className={someFilteredSelected && !allFilteredSelected ? 'opacity-70' : ''}
                    />
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium">Utente</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">Stato</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">Inizio</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">Scadenza</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">Giorni Rim.</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">Note</th>
                  <th className="px-4 py-3 text-right text-sm font-medium">Azioni</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {filteredUsers.map((user) => {
                  const daysRemaining = getDaysRemaining(user.subscription_end_date)
                  const isSelected = selectedIds.has(user.user_id)
                  return (
                    <tr
                      key={user.user_id}
                      className={`hover:bg-muted/30 transition-colors ${isSelected ? 'bg-blue-500/5' : ''}`}
                    >
                      <td className="px-4 py-3">
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={() => toggleSelectUser(user.user_id)}
                          aria-label={`Seleziona ${user.email}`}
                        />
                      </td>
                      <td className="px-4 py-3">
                        <div>
                          <p className="font-medium">{user.email}</p>
                          {user.full_name && (
                            <p className="text-sm text-muted-foreground">{user.full_name}</p>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">{getStatusBadge(user.subscription_status)}</td>
                      <td className="px-4 py-3 text-sm">{formatDate(user.subscription_start_date)}</td>
                      <td className="px-4 py-3 text-sm">{formatDate(user.subscription_end_date)}</td>
                      <td className="px-4 py-3">
                        {daysRemaining !== null && (
                          <span className={`text-sm font-medium ${
                            daysRemaining <= 0  ? 'text-red-500' :
                            daysRemaining <= 7  ? 'text-yellow-500' :
                            'text-green-500'
                          }`}>
                            {daysRemaining <= 0 ? 'Scaduto' : `${daysRemaining}g`}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-muted-foreground max-w-[200px] truncate">
                        {user.subscription_notes || '-'}
                      </td>
                      <td className="px-4 py-3 text-right space-x-2">
                        <Button
                          size="sm"
                          variant={user.ixen_system_access ? "default" : "outline"}
                          onClick={() => handleToggleIxenSystemAccess(user.user_id)}
                          title={user.ixen_system_access ? "Revoca accesso iXen System" : "Attiva accesso iXen System"}
                          className={user.ixen_system_access ? "bg-blue-600 hover:bg-blue-700" : ""}
                        >
                          <Monitor className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant={user.emergency_mode ? "destructive" : "outline"}
                          onClick={() => handleToggleEmergency(user.user_id)}
                          title={user.emergency_mode ? "Disattiva modalità emergenza" : "Attiva modalità emergenza"}
                        >
                          <AlertTriangle className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleEditUser(user)}
                        >
                          Modifica
                        </Button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>

            {filteredUsers.length === 0 && (
              <div className="p-8 text-center text-muted-foreground">
                Nessun utente trovato
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Dialog modifica singolo utente */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-[500px] max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Modifica Abbonamento</DialogTitle>
            <DialogDescription>{selectedUser?.email}</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Stato Abbonamento</Label>
              <Select
                value={updateData.status}
                onValueChange={(value) => setUpdateData({...updateData, status: value})}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Attivo</SelectItem>
                  <SelectItem value="inactive">Inattivo</SelectItem>
                  <SelectItem value="expired">Scaduto</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {updateData.status === 'active' && (
              <div className="space-y-2">
                <Label>Durata (giorni)</Label>
                <Select
                  value={String(updateData.extend_days || 30)}
                  onValueChange={(value) => setUpdateData({...updateData, extend_days: parseInt(value)})}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="30">30 giorni (1 mese)</SelectItem>
                    <SelectItem value="90">90 giorni (3 mesi)</SelectItem>
                    <SelectItem value="180">180 giorni (6 mesi)</SelectItem>
                    <SelectItem value="365">365 giorni (1 anno)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  L'abbonamento verrà esteso di {updateData.extend_days || 30} giorni dalla data odierna
                </p>
              </div>
            )}

            <div className="space-y-2">
              <Label>Note (opzionale)</Label>
              <Textarea
                placeholder="Note interne sull'abbonamento..."
                value={updateData.notes || ''}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setUpdateData({...updateData, notes: e.target.value})}
                rows={3}
              />
            </div>

            <div className="space-y-2 pt-4 border-t">
              <Label className="text-base font-semibold">Accesso iXen System</Label>
              <p className="text-xs text-muted-foreground mb-2">
                Permette all'utente di accedere alla pagina iXen System (/trading/system)
              </p>
              <div className="flex items-center justify-between p-3 border rounded-lg bg-muted/30">
                <div className="flex items-center gap-2">
                  <Monitor className="w-4 h-4" />
                  <span className="font-medium">iXen System</span>
                  {selectedUser?.ixen_system_access ? (
                    <Badge className="bg-blue-500 hover:bg-blue-600 text-xs">Attivo</Badge>
                  ) : (
                    <Badge variant="outline" className="text-xs">Disattivo</Badge>
                  )}
                </div>
                <Button
                  size="sm"
                  variant={selectedUser?.ixen_system_access ? "destructive" : "default"}
                  onClick={() => selectedUser && handleToggleIxenSystemAccess(selectedUser.user_id)}
                >
                  {selectedUser?.ixen_system_access ? 'Revoca' : 'Attiva'}
                </Button>
              </div>
            </div>

            <div className="space-y-2 pt-4 border-t">
              <Label className="text-base font-semibold">Accesso Provider</Label>
              <p className="text-xs text-muted-foreground mb-2">
                Gestisci l'accesso ai provider a pagamento per questo utente
              </p>
              {loadingProviders ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="w-5 h-5 animate-spin" />
                </div>
              ) : (
                <div className="space-y-2">
                  {providerAccess.filter(p => !p.is_default_included).map((provider) => (
                    <div key={provider.provider_code} className="flex items-center justify-between p-3 border rounded-lg bg-muted/30">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{provider.provider_name}</span>
                          <Badge variant="outline" className="text-xs">€{provider.one_time_price}</Badge>
                        </div>
                        {provider.access_type && (
                          <p className="text-xs text-muted-foreground mt-1">
                            {provider.access_type === 'granted' && provider.granted_by_admin
                              ? `Attivato da ${provider.granted_by_admin}`
                              : provider.access_type === 'purchased'
                                ? "Acquistato dall'utente"
                                : provider.access_type}
                          </p>
                        )}
                      </div>
                      <Button
                        size="sm"
                        variant={provider.has_access ? "destructive" : "default"}
                        onClick={() => handleToggleProviderAccess(provider.provider_code, provider.has_access)}
                        disabled={togglingProvider === provider.provider_code}
                      >
                        {togglingProvider === provider.provider_code ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : provider.has_access ? 'Revoca' : 'Attiva'}
                      </Button>
                    </div>
                  ))}
                  {providerAccess.filter(p => !p.is_default_included).length === 0 && (
                    <p className="text-sm text-muted-foreground text-center py-2">
                      Nessun provider a pagamento disponibile
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
              Annulla
            </Button>
            <Button onClick={handleUpdateSubscription} disabled={updating}>
              {updating ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Salvataggio...</>
              ) : 'Salva'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
